/*
 * rheincool — Anlagen-Konfigurator (Konfigurator-Handoff.md)
 *
 * Räume statt Produkte: Anzahl Innengeräte (1–5) + Leistung je Raum.
 * Matching gegen die inline gerenderten Multisplit-Anlagen:
 *   anzahl_innengeraete ≥ Auswahl UND aussengeraet_kw ≥ Summe × Toleranzfaktor
 *   UND (falls kombinierbare_leistungen gepflegt) jede Raum-kW enthalten.
 * Trefferzahl + Ab-Preis erscheinen VOR dem Klick (aria-live). Null-Treffer
 * zeigt den nächstgelegenen Vorschlag + Beratungs-CTA — nie Sackgasse.
 * Konfiguration reist über URL-Parameter (?innengeraete=3&kw=3,5;2,5;2,0)
 * und sessionStorage; Aufklapp-Zustand in localStorage (SPEICHER).
 */

/* Wird von zwei Sections eingebunden. Stehen zwei davon auf derselben Seite,
   laeuft das Skript zweimal — `class` warf dann einen SyntaxError
   „already declared" und der zweite Durchlauf brach ab. Der Guard sorgt
   dafuer, dass sich das Modul genau einmal definiert. */
if (!customElements.get('rc-configurator')) {
const SPEICHER = 'rc_configurator_open';

class RcConfigurator extends HTMLElement {
  /* Zwei Quellen, eine Form.

     `data-config-index` ist der vorberechnete, VOLLSTAENDIGE Bestand aus dem
     Collection-Metafeld: {m:[Marken], a:[[handle, n, kW, markeIdx, [kombis]]]}.
     `data-config-anlagen` ist die alte, in Liquid erzeugte Liste — die kann
     hoechstens 250 Produkte fassen und dient nur noch als Rueckfall, falls das
     Metafeld fehlt. Beide werden zu {n, kw, v, u, k} normalisiert. */
  ladeAnlagen(doc) {
    if (doc) {
      return doc.a.map((r) => ({
        u: `/products/${r[0]}`,
        n: r[1],
        kw: r[2],
        v: doc.m[r[3]] || '',
        k: r[4] || null,
      }));
    }
    const roh = this.querySelector('[data-config-anlagen]');
    return roh ? JSON.parse(roh.textContent) : [];
  }

  /* Monosplit-Anlagen fuer den Ein-Raum-Fall: [[handle, kW], ...].
     Bewusst eine eigene, schmale Liste — bei einem Raum braucht die Regel
     weder Geraetezahl noch Marke, nur die Leistung. */
  ladeMono(doc) {
    if (!doc || !doc.a) return [];
    return doc.a.map((r) => ({ u: `/products/${r[0]}`, kw: r[1], n: 1, v: doc.m[r[2]] || '' }));
  }

  /* Die beiden Indizes liegen seit dem 31.08.2026 nicht mehr als Inline-JSON
     im Dokument, sondern unter `?view=konfig` — 55 kB weniger je Seite, und
     der Browser haelt sie ueber mehrere Seiten hinweg im Cache. Schlaegt der
     Abruf fehl, bleibt der in Liquid erzeugte Rueckfall (bei 250 Produkten
     gedeckelt); ohne den beides leer, dann meldet das Modul „keine Treffer"
     statt zu brechen. */
  async holeIndex() {
    const url = this.dataset.indexUrl;
    if (!url) return { multi: null, mono: null };
    try {
      /* Ohne Accept-Header: mit `Accept: application/json` liefert Shopify
         seine eigene Collection-API statt der Ansicht — 377 Bytes Metadaten
         statt der 56 kB Indexdaten, der Konfigurator stand dann auf null. */
      const antwort = await fetch(url);
      if (!antwort.ok) throw new Error(antwort.status);
      return await antwort.json();
    } catch {
      return { multi: null, mono: null };
    }
  }

  /* Der Bestand, gegen den gerechnet wird — Monosplit bei einem Raum, sonst
     Multisplit. Beide Listen haben dieselbe Form, damit alles darueber
     (Marken zaehlen, Raster filtern, Vorschlag) unveraendert funktioniert. */
  bestand(n) {
    return n === 1 ? this.mono : this.anlagen;
  }

  connectedCallback() {
    this.aufbauen();
  }

  async aufbauen() {
    try {
      this.stufen = JSON.parse(this.querySelector('[data-config-stufen]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-config-strings]').textContent);
      const daten = await this.holeIndex();
      this.anlagen = this.ladeAnlagen(daten.multi);
      this.mono = this.ladeMono(daten.mono);
    } catch {
      return;
    }
    if (!this.stufen.length) return;

    this.details = this.closest('details');
    this.ort = this.details?.dataset.ort || 'collection';
    this.maxUnits = parseInt(this.dataset.maxUnits, 10) || 5;
    /* Leistungsfenster, an den echten Daten gemessen (29.08.2026, 367 Sets):
       kW-Aussen / Summe-Innen liegt zwischen 0,61 und 1,70, Median 0,73,
       P90 1,00. Die Untergrenze 0,60 sitzt knapp unter dem Katalogminimum —
       tiefer waere sinnlos, hoeher sperrt eigenes Sortiment aus. Die
       Obergrenze 1,20 haelt die Ueberdimensionierten draussen, laesst aber
       die Trial-Sets mit 1,13 durch. */
    this.tolerance = parseFloat(this.dataset.tolerance) || 0.6;
    this.toleranceMax = parseFloat(this.dataset.toleranceMax) || 1.2;
    /* Ab zwei Innengeraeten: der Zielbestand fuehrt KEINE einzige Anlage mit
       nur einem Innengeraet. Die Stufe „1" lieferte mit der neuen, exakten
       Regel garantiert null Treffer und eine komplett graue Markenzeile. Wer
       einen Raum kuehlen will, gehoert zu den Monosplit-Anlagen. */
    this.minUnits = parseInt(this.dataset.minUnits, 10) || 1;
    this.onTarget = this.dataset.onTarget === 'true';

    this.unitsBox = this.querySelector('[data-units]');
    this.roomsBox = this.querySelector('[data-rooms]');
    this.suggestionEl = this.querySelector('[data-suggestion]');
    this.contextEl = this.querySelector('[data-context]');
    this.cta = this.querySelector('[data-cta]');
    // Faellt auf den Link selbst zurueck, falls das Markup noch ohne Spanne ist.
    this.ctaText = this.querySelector('[data-cta-text]') || this.cta;
    this.markenBox = this.querySelector('[data-marken]');
    this.markenHinweis = this.querySelector('[data-marken-hinweis]');
    this.markenHinweisNone = this.querySelector('[data-marken-hinweis-none]');
    this.einraumEl = this.querySelector('[data-einraum]');
    this.state = this.restoreState();
    this.active = new URLSearchParams(location.search).has('innengeraete');
    this.renderMarken();
    this.renderUnits();
    this.renderRooms();
    this.update();

    // Auf der Ergebnisseite die Liste tatsaechlich einschraenken. Bisher
    // reisten die Parameter zwar mit, das Raster zeigte aber weiter die
    // komplette Collection — wer vier Fragen beantwortet hatte, bekam die
    // ungefilterte Liste.
    if (this.active) this.filtereRaster();

    this.setupDetails();
    this.verbindeHeroCta();
    const reset = this.details?.querySelector('[data-config-reset]');
    if (reset) {
      reset.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.resetState();
      });
    }
    this.cta.addEventListener('click', (event) => this.onApply(event));
  }

  /* Ein-Knopf-Loesung mobil (Freigabe 02.09.2026): Der orange Hero-Knopf
     oeffnet auf schmalen Viewports den Konfigurator, statt zur Collection
     zu springen — der zugeklappte Balken ist mobil ausgeblendet
     (section-configurator.css), der Knopf ist der einzige Einstieg.
     Am Desktop bleibt der Knopf ein normaler Link; die Breite wird beim
     Klick geprueft, nicht beim Laden (Drehung des Geraets). Scroll und
     Speichern uebernimmt der bestehende toggle-Listener. */
  verbindeHeroCta() {
    if (this.ort !== 'home' || !this.details) return;
    const cta = document.querySelector('.rc-hero2__cta-primary[data-cta-konfigurator]');
    if (!cta) return;
    cta.addEventListener('click', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      event.preventDefault();
      this.details.open = true;
      if (window.rcTrack) window.rcTrack('configurator_open', { ort: 'hero-cta' });
    });
  }

  /* ---------- Zustand ---------- */

  defaultState() {
    /* Standard seit 02.09.2026 (Haendlerwahl): 1 Innengeraet mit 3,5 kW —
       der haeufigste Fall (ein Wohnraum) statt der frueheren 3 Geraete mit
       2,5 kW. Die 3,5-kW-Stufe wird gesucht, nicht ueber ihren Index
       angesprochen: die Stufenliste ist Editor-konfigurierbar. */
    const wunsch = this.stufen.find((s) => Math.abs(s.kw - 3.5) < 0.05);
    const kw = wunsch ? wunsch.kw : (this.stufen[1] ? this.stufen[1].kw : this.stufen[0].kw);
    const n = Math.max(1, this.minUnits);
    return { n, rooms: Array.from({ length: n }, () => kw), marken: this.markenVorauswahl(n) };
  }

  /* Standard-Vorauswahl der Marken aus dem Markup (data-marken-vorauswahl).
     Nur Marken, die im jeweiligen Zielbestand tatsaechlich vorkommen — eine
     Geistermarke wuerde still auf null Treffer filtern. Bewusst gegen den
     Bestand geprueft statt gegen markenListe(): die liest this.state.n, und
     den Zustand gibt es hier noch nicht. Greift nur im frischen Zustand;
     URL-Parameter und die eigene Wahl aus sessionStorage gehen vor. */
  markenVorauswahl(n) {
    const roh = (this.dataset.markenVorauswahl || '')
      .split(',').map((s) => s.trim()).filter(Boolean);
    if (!roh.length) return [];
    const vorhanden = new Set(this.bestand(n).map((a) => a.v).filter(Boolean));
    return roh.filter((name) => vorhanden.has(name));
  }

  /* Nur Marken aus data-marken-reihenfolge uebernehmen (19.09.2026: Sortiment
     auf vier Marken). Ein alter Link mit ?filter.p.vendor=Samsung oder eine
     Wahl aus derselben Sitzung ergaebe sonst 0 Treffer ohne Chip zum Abwaehlen. */
  erlaubteMarken(marken) {
    const erlaubt = new Set((this.dataset.markenReihenfolge || '').split(',').map((s) => s.trim()).filter(Boolean));
    return erlaubt.size ? marken.filter((m) => erlaubt.has(m)) : marken;
  }

  restoreState() {
    // 1. URL-Parameter, 2. sessionStorage, 3. Default (3 Räume à 2,5 kW)
    const params = new URLSearchParams(location.search);
    if (params.has('innengeraete')) {
      const n = Math.min(this.maxUnits, Math.max(this.minUnits, parseInt(params.get('innengeraete'), 10) || 1));
      const kws = (params.get('kw') || '')
        .split(';')
        .map((v) => parseFloat(v.replace(',', '.')))
        .filter((v) => !Number.isNaN(v));
      const rooms = Array.from({ length: n }, (_, i) => kws[i] ?? this.stufen[1]?.kw ?? this.stufen[0].kw);
      const marken = this.erlaubteMarken(params.getAll('filter.p.vendor').map((v) => v.trim()).filter(Boolean));
      return { n, rooms, marken };
    }
    try {
      const saved = JSON.parse(sessionStorage.getItem('rc-configurator'));
      if (saved && saved.n && Array.isArray(saved.rooms)) {
        saved.marken = this.erlaubteMarken(Array.isArray(saved.marken) ? saved.marken : []);
        /* Ein aelterer Zustand kann eine Geraetezahl unterhalb der heutigen
           Untergrenze tragen — dann waere der Waehler ohne markierte Stufe. */
        if (saved.n < this.minUnits) return this.defaultState();
        return saved;
      }
    } catch {
      /* leerer Zustand */
    }
    return this.defaultState();
  }

  persist() {
    sessionStorage.setItem('rc-configurator', JSON.stringify(this.state));
  }

  resetState() {
    this.state = this.defaultState();
    sessionStorage.removeItem('rc-configurator');
    const params = new URLSearchParams(location.search);
    if (params.has('innengeraete')) {
      params.delete('innengeraete');
      params.delete('kw');
      const qs = params.toString();
      history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : ''));
    }
    this.active = false;
    document.querySelectorAll('[data-product-grid] > *').forEach((el) => { el.hidden = false; });
    this.renderUnits();
    this.renderRooms();
    this.update();
    if (window.rcTrack) window.rcTrack('configurator_reset', { ort: this.ort });
  }

  /* ---------- Aufklapp-Zustand ---------- */

  setupDetails() {
    if (!this.details) return;
    // Alter Speicherschluessel: darunter stand bei Besuchern, die einmal auf
    // einer Ergebnisseite waren, ein "true", das sie nie selbst gewaehlt
    // hatten. Er wird weiterhin verworfen.
    localStorage.removeItem('configurator_open');

    // Standard ist AUFGEKLAPPT (Haendlerwunsch 29.08.2026), auf jeder Seite
    // und auf jedem Geraet. Zu bleibt er nur fuer den, der ihn selbst
    // zugeklappt hat; diese Wahl steht im localStorage und gilt
    // seitenuebergreifend.
    //
    // Damit sind drei fruehere Ausnahmen entfallen: mobil (wo das
    // aufgeklappte Formular die erste Bildschirmseite fuellt), auf der
    // Ergebnisseite (wo der Besucher seine Auswahl schon getroffen hat) und
    // bei aktivem Filter. Wer eine davon zurueck will, prueft sie hier —
    // nicht im Markup, sonst blitzt der Kasten beim Laden auf.
    const saved = localStorage.getItem(SPEICHER);
    this.details.open = saved !== 'false';

    /* Mobile Ausnahme, zurueck seit 02.09.2026 (Haendlerwunsch, mobile
       Startseiten-Folge): Ohne eigene gespeicherte Wahl startet der Kasten
       auf schmalen Viewports ZU — aufgeklappt fuellt das Formular die
       erste Bildschirmseite. Wer ihn selbst geoeffnet hat (saved 'true'),
       behaelt ihn offen; genau so steht es im Kommentar oben vorgesehen. */
    if (saved === null && window.matchMedia('(max-width: 749px)').matches) {
      this.details.open = false;
    }

    /* Ausnahme Ergebnisseite: Wer mit ?innengeraete=… ankommt, hat den
       Konfigurator gerade benutzt — er will die Treffer sehen, nicht das
       Formular noch einmal. Der Kasten startet dann zu; die Auswahl steht
       als Zusammenfassung in der Kopfzeile, ein Klick oeffnet ihn wieder.
       Bewusst OHNE localStorage-Schreiben: das ist Kontext, keine Wahl des
       Besuchers — auf der naechsten Seite gilt wieder sein eigener Stand. */
    if (this.ort === 'collection' && new URLSearchParams(location.search).has('innengeraete')) {
      this.details.open = false;
    }

    this.zuletztGesetzt = this.details.open;
    this.details.addEventListener('toggle', () => {
      if (this.details.open === this.zuletztGesetzt) return;
      this.zuletztGesetzt = this.details.open;
      localStorage.setItem(SPEICHER, this.details.open ? 'true' : 'false');
      if (this.details.open) {
        // Scroll-Anchoring springt sonst ans Ende des aufgeklappten Inhalts —
        // wir stellen sicher, dass der Anfang (Kopfzeile) sichtbar ist.
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        requestAnimationFrame(() => {
          this.details.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
        });
      }
      if (window.rcTrack) {
        window.rcTrack(this.details.open ? 'configurator_open' : 'configurator_collapse', { ort: this.ort });
      }
    });
  }

  /* ---------- Rendering ---------- */

  formatKw(kw) {
    return kw.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }

  renderUnits() {
    this.unitsBox.innerHTML = '';
    for (let i = this.minUnits; i <= this.maxUnits; i += 1) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rc-config__unit rc-num';
      btn.setAttribute('role', 'radio');
      btn.textContent = i;
      btn.addEventListener('click', () => this.setUnits(i));
      btn.addEventListener('keydown', (event) =>
        this.onRadioKey(event, this.unitsBox, i - this.minUnits, (idx) => this.setUnits(idx + this.minUnits)));
      this.unitsBox.appendChild(btn);
    }
    this.syncUnits();
  }

  syncUnits() {
    [...this.unitsBox.children].forEach((btn, i) => {
      const selected = i + this.minUnits === this.state.n;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      btn.tabIndex = selected ? 0 : -1;
    });
    if (this.contextEl) {
      this.contextEl.textContent = this.strings.context.replace('[n]', this.state.n);
      this.contextEl.hidden = this.state.n < 2;
    }
    if (this.einraumEl) {
      this.einraumEl.textContent = this.strings.one_room_note || '';
      this.einraumEl.hidden = this.state.n !== 1;
    }
  }

  setUnits(n) {
    const prev = this.state.rooms;
    // Räume beim Erhöhen mit Default anlegen, beim Verringern hinten entfernen —
    // bestehende Auswahl bleibt (Handoff §3)
    const defaultKw = this.stufen[1]?.kw ?? this.stufen[0].kw;
    this.state.n = n;
    this.state.rooms = Array.from({ length: n }, (_, i) => prev[i] ?? defaultKw);
    this.syncUnits();
    /* Wechsel zwischen einem und mehreren Raeumen heisst Wechsel des
       Bestands. Eine Marke, die es dort nicht gibt, bliebe sonst als
       unsichtbarer Filter stehen und ergaebe null Treffer. */
    if ((prev.length === 1) !== (n === 1) && this.state.marken && this.state.marken.length) {
      const erlaubt = new Set(this.bestand(n).map((a) => a.v));
      this.state.marken = this.state.marken.filter((m) => erlaubt.has(m));
    }
    this.renderMarken();
    this.renderRooms();
    this.update();
    if (window.rcTrack) window.rcTrack('configurator_units_select', { anzahl: n });
  }

  renderRooms() {
    this.roomsBox.innerHTML = '';
    this.state.rooms.forEach((kw, idx) => {
      const card = document.createElement('div');
      card.className = 'rc-config__room';
      const head = document.createElement('div');
      head.className = 'rc-config__room-head';
      head.innerHTML = `<b>${this.strings.room.replace('[n]', idx + 1)}</b><span>${this.strings.room_hint}</span>`;
      card.appendChild(head);

      const group = document.createElement('div');
      group.className = 'rc-config__powers';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', this.strings.room_aria.replace('[n]', idx + 1));
      this.stufen.forEach((stufe, sIdx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rc-config__power';
        btn.setAttribute('role', 'radio');
        btn.innerHTML = `<b class="rc-num">${this.formatKw(stufe.kw)} kW</b><span>${this.strings.m2_to.replace('[m2]', stufe.m2)}</span>`;
        btn.addEventListener('click', () => this.setRoomPower(idx, stufe.kw));
        btn.addEventListener('keydown', (event) =>
          this.onRadioKey(event, group, sIdx, (next) => this.setRoomPower(idx, this.stufen[next].kw))
        );
        group.appendChild(btn);
      });
      card.appendChild(group);
      this.roomsBox.appendChild(card);
    });
    this.syncRooms();
  }

  syncRooms() {
    [...this.roomsBox.children].forEach((card, roomIdx) => {
      const buttons = card.querySelectorAll('.rc-config__power');
      buttons.forEach((btn, sIdx) => {
        const selected = this.stufen[sIdx].kw === this.state.rooms[roomIdx];
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-checked', selected ? 'true' : 'false');
        btn.tabIndex = selected ? 0 : -1;
      });
    });
  }

  setRoomPower(roomIdx, kw) {
    this.state.rooms[roomIdx] = kw;
    this.syncRooms();
    /* Auch die Marken neu zaehlen: ihre Trefferzahl haengt an der
       Gesamtleistung, nicht nur an der Geraetezahl. Ohne das stand bei fuenf
       Raeumen zu 5 kW weiter „Alle Marken 52", waehrend der Knopf darunter
       schon „Beratung anfragen" zeigte. */
    this.renderMarken();
    this.update();
    if (window.rcTrack) window.rcTrack('configurator_power_select', { raum: roomIdx + 1, kw });
  }

  /* Einzahl statt „1 Anlagen". Die Locale fuehrt zu jedem Zaehl-Satz eine
     zweite Fassung mit dem Suffix _one; fehlt sie, bleibt es beim Plural. */
  satz(schluessel, anzahl) {
    if (anzahl === 1 && this.strings[`${schluessel}_one`]) return this.strings[`${schluessel}_one`];
    return this.strings[schluessel] || '';
  }

  /* WAI-ARIA-Radiogroup: Pfeiltasten wechseln */
  onRadioKey(event, group, index, select) {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const count = group.querySelectorAll('[role="radio"]').length;
    const dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = (index + dir + count) % count;
    select(next);
    group.querySelectorAll('[role="radio"]')[next]?.focus();
  }

  /* ---------- Marken ---------- */

  /* Welche Marken einen eigenen Chip bekommen — und in welcher Reihenfolge.

     Die Folge steht im Markup (data-marken-reihenfolge) und ist bewusst fest,
     nicht nach Bestand sortiert: der Haendler will seine Hauptmarken immer an
     derselben Stelle sehen, und eine nach Trefferzahl sortierte Zeile ordnete
     sich bei jedem Klick neu. Marken ausserhalb der Liste bleiben ueber „Alle
     Marken" im Ergebnis — sie bekommen nur keinen Knopf.

     Marken ohne jede Anlage im Zielbestand bleiben in der Zeile und werden
     als dauerhaft leer ausgezeichnet (siehe renderMarken). Sie stillschweigend
     wegzulassen war die schlechtere Loesung: der Haendler fuehrt LG mit 188
     Geraeten, nur eben keine Multisplit-Anlage — das ist eine Aussage, die
     der Kunde sehen darf, kein Grund zum Verstecken. */
  markenListe() {
    /* Zwei Bestaende, zwei Markenlisten: LG fuehrt 28 Monosplit-Anlagen, aber
       keine einzige Multisplit-Anlage. Ein gemeinsamer Cache haette bei einem
       Raum die Multisplit-Zahlen gezeigt. */
    const schluessel = this.state.n === 1 ? 'mono' : 'multi';
    this._markenListen = this._markenListen || {};
    if (this._markenListen[schluessel]) return this._markenListen[schluessel];
    const gesamt = new Map();
    this.bestand(this.state.n).forEach((a) => {
      const marke = a.v || '';
      if (!marke) return;
      gesamt.set(marke, (gesamt.get(marke) || 0) + 1);
    });
    const folge = (this.dataset.markenReihenfolge || '')
      .split(',').map((m) => m.trim()).filter(Boolean);
    if (folge.length) {
      this._markenListen[schluessel] = folge.map((name) => ({ name, gesamt: gesamt.get(name) || 0 }));
    } else {
      this._markenListen[schluessel] = [...gesamt.entries()]
        .map(([name, anzahl]) => ({ name, gesamt: anzahl }))
        .sort((a, b) => b.gesamt - a.gesamt || a.name.localeCompare(b.name, 'de'));
    }
    return this._markenListen[schluessel];
  }

  /* Trefferzahl je Marke fuer die AKTUELLE Konfiguration.

     Vorher zaehlte hier ein vorberechneter Index nur die Anschluesse
     („fuehrt die Marke eine Anlage mit mindestens n Innengeraeten?"), waehrend
     der Knopf darunter mit der vollen Regel inklusive Leistung rechnete. Die
     Chips summierten sich dadurch auf 140, der Knopf nannte 130 — zwei Zahlen
     fuer dieselbe Auswahl. Jetzt kommen beide aus matches(). */
  trefferProMarke() {
    const proMarke = new Map();
    this.matches({ n: this.state.n, rooms: this.state.rooms, marken: [] }).forEach((a) => {
      proMarke.set(a.v || '', (proMarke.get(a.v || '') || 0) + 1);
    });
    return proMarke;
  }

  /* „Mitsubishi Electric" liegt als brand-mitsubishi.webp — der Dateiname
     ist nicht ableitbar, die Zuordnung kommt aus dem Markup. */
  logoDatei(marke) {
    if (!this._logos) {
      this._logos = new Map();
      (this.dataset.markenLogos || '').split(',').forEach((paar) => {
        const [name, datei] = paar.split('|');
        if (name && datei) this._logos.set(name.trim(), datei.trim());
      });
    }
    return this._logos.get(marke) || null;
  }

  renderMarken() {
    if (!this.markenBox) return;

    const gewaehlt = new Set(this.state.marken || []);
    const n = this.state.n;
    const frag = document.createDocumentFragment();
    const proMarke = this.trefferProMarke();

    /* Zwei verschiedene Leerzustaende, getrennt gezaehlt und getrennt benannt:
       „fuehrt gerade nichts mit dieser Geraetezahl" loest sich mit einem Klick
       auf eine andere Zahl, „fuehrt ueberhaupt keine Multisplit-Anlage" nicht. */
    const leerUnits = [];
    const leerSortiment = [];

    const alle = document.createElement('button');
    alle.type = 'button';
    alle.className = 'rc-config__marke' + (gewaehlt.size === 0 ? ' is-selected' : '');
    alle.setAttribute('aria-pressed', gewaehlt.size === 0 ? 'true' : 'false');
    alle.dataset.marke = '';
    /* Ohne Trefferzahl am Chip (Haendlerwunsch 29.08.2026): die Zahl stand
       direkt darunter noch einmal auf dem Knopf, und bei „Alle Marken" war es
       woertlich dieselbe. Gerechnet wird sie weiter — sie entscheidet, welche
       Marke nicht waehlbar ist. */
    alle.innerHTML = `<span class="rc-config__marke-name">${this.strings.brand_all || 'Alle Marken'}</span>`;
    frag.appendChild(alle);

    this.markenListe().forEach((m) => {
      const anzahl = proMarke.get(m.name) || 0;
      const imSortiment = m.gesamt > 0;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'rc-config__marke' + (gewaehlt.has(m.name) ? ' is-selected' : '');
      b.dataset.marke = m.name;
      b.setAttribute('aria-pressed', gewaehlt.has(m.name) ? 'true' : 'false');

      let zustand = '';
      /* aria-disabled statt disabled: ein disabled-Button faellt aus der
         Tabreihenfolge und wird von Screenreadern uebersprungen. Wer nicht
         sieht, dass der Chip zurueckgenommen ist, erfuehre sonst gar nicht,
         dass es die Marke gibt. Den Klick faengt toggleMarke() ab. */
      if (!anzahl && !gewaehlt.has(m.name)) {
        b.setAttribute('aria-disabled', 'true');
        b.dataset.leer = imSortiment ? 'units' : 'sortiment';
        if (imSortiment) {
          leerUnits.push(m.name);
          zustand = (this.strings.brand_state_units || '').replace('[units]', n);
        } else {
          leerSortiment.push(m.name);
          zustand = this.strings.brand_state_none || '';
        }
      }

      const datei = this.logoDatei(m.name);
      /* width/height am Bild, obwohl die CSS eine feste Box setzt: ohne die
         Attribute hat das Bild bis zum Laden keine Eigengroesse und die
         Chipbreite springt. */
      const logo = datei
        ? `<img class="rc-config__marke-logo" src="${this.dataset.assetBase}${datei}" alt="${m.name}" width="70" height="22" loading="lazy">`
        : `<span class="rc-config__marke-name">${m.name}</span>`;
      /* Der Zustand steht nur fuer Screenreader dabei. Als aria-label wuerde
         er den Markennamen aus dem Alt-Text verdraengen. */
      b.innerHTML = logo + (zustand ? `<span class="visually-hidden">${zustand}</span>` : '');
      frag.appendChild(b);
    });

    /* Vor dem Austausch merken, welcher Chip den Fokus hat. replaceChildren
       baut alle Knoepfe neu — wer per Tastatur eine Marke waehlt, verlor den
       Fokus danach an <body> und musste sich von vorn durchtabben. */
    const fokus = document.activeElement;
    const fokusMarke = fokus && this.markenBox.contains(fokus) ? fokus.dataset.marke : null;

    this.markenBox.replaceChildren(frag);
    this.markenBox.querySelectorAll('[data-marke]').forEach((b) => {
      b.addEventListener('click', () => this.toggleMarke(b.dataset.marke, b));
    });

    if (fokusMarke !== null && fokusMarke !== undefined) {
      const wieder = this.markenBox.querySelector(`[data-marke="${CSS.escape(fokusMarke)}"]`);
      if (wieder) wieder.focus();
    }

    /* Bei einem Raum liegt es nie an der Geraetezahl, sondern an der
       Leistung — „keine Anlage mit 1 Innengeraeten" waere falsch und wuerde
       den Kunden in die falsche Richtung schicken. */
    this.zeigeHinweis(this.markenHinweis, leerUnits, n === 1 ? 'brand_hint_kw' : 'brand_hint', n);
    this.zeigeHinweis(this.markenHinweisNone, leerSortiment, 'brand_hint_none', n);
  }

  /* Eine Hinweiszeile fuellen oder verstecken. Nennt die Marken beim Namen
     statt nur ihre Anzahl — „2 Marken sind nicht waehlbar" zwingt den Kunden,
     die graue Zeile selbst abzusuchen. */
  zeigeHinweis(el, marken, schluessel, n) {
    if (!el) return;
    el.hidden = marken.length === 0;
    if (!marken.length) return;
    el.textContent = this.satz(schluessel, marken.length)
      .replace('[marken]', marken.join(', '))
      .replace('[units]', n);
  }

  toggleMarke(name, knopf) {
    if (knopf && knopf.getAttribute('aria-disabled') === 'true') return;
    const set = new Set(this.state.marken || []);
    if (!name) set.clear();
    else if (set.has(name)) set.delete(name);
    else set.add(name);
    this.state.marken = [...set];
    this.renderMarken();
    this.update();
    if (window.rcTrack) window.rcTrack('configurator_brand_select', { marken: this.state.marken.join(';') || 'alle' });
  }

  /* ---------- Matching ---------- */

  sum() {
    return this.state.rooms.reduce((acc, kw) => acc + kw, 0);
  }

  matches(config) {
    const total = config.rooms.reduce((a, b) => a + b, 0);

    /* Ein Raum ist ein anderer Warenkorb: Monosplit, nicht Multisplit. Die
       Multisplit-Collection fuehrt dafuer nichts, deshalb rechnet die Regel
       hier gegen den Monosplit-Index. Kein Gleichzeitigkeitsabschlag — bei
       einem Innengeraet gibt es nichts zu verteilen, das Aussengeraet muss die
       Leistung wirklich bringen. Untergrenze deshalb 0,85 statt 0,60. */
    if (config.n === 1) {
      const nurMarken = config.marken && config.marken.length ? new Set(config.marken) : null;
      return this.mono.filter((a) => {
        if (nurMarken && !nurMarken.has(a.v)) return false;
        return a.kw >= total * 0.85 && a.kw <= total * this.toleranceMax;
      });
    }

    const marken = config.marken && config.marken.length ? new Set(config.marken) : null;
    return this.anlagen.filter((a) => {
      if (marken && !marken.has(a.v)) return false;

      /* EXAKT die gewaehlte Zahl, nicht „mindestens" (Haendlerwunsch
         29.08.2026). Vorher passte ein Vierfachsystem auf drei Raeume — der
         Kunde bekam Anlagen angeboten, bei denen ein Anschluss leer bleibt.
         Das ist der grosse Hebel: 375 Anlagen fallen auf hoechstens 129. */
      if (a.n !== config.n) return false;

      /* Fenster statt blosser Untergrenze. Ohne Obergrenze passte ein
         13-kW-Aussengeraet auf 7,5 kW Bedarf. */
      if (a.kw < total * this.tolerance) return false;
      if (a.kw > total * this.toleranceMax) return false;

      /* Die Pruefung gegen custom.kombinierbare_leistungen ist entfallen:
         das Feld ist bei 0 von 375 Anlagen gepflegt — shopweit
         metafieldsCount 0 — der Zweig hat seit dem ersten Tag nie eine
         Anlage aussortiert. Wird das Feld eines Tages gepflegt, gehoert die
         Pruefung zurueck; solange es leer ist, taeuscht sie Strenge vor. */
      return true;
    });
  }

  filtereRaster() {
    const raster = document.querySelector('[data-product-grid]');
    if (!raster) return;
    const treffer = this.matches(this.state);
    if (!treffer.length) {
      /* Frueher stand hier ein `return` — der Kunde bekam dann die
         UNGEFILTERTE Liste zu sehen, obwohl nichts passt. */
      this.rasterHinweis(raster, 0, 0);
      return;
    }

    const pfade = new Set(treffer.map((a) => (a.u || '').split('?')[0]));
    let sichtbar = 0;
    [...raster.children].forEach((el) => {
      const link = el.querySelector('a[href*="/products/"]');
      const pfad = link ? link.getAttribute('href').split('?')[0] : '';
      const passt = pfade.has(pfad);
      el.hidden = !passt;
      if (passt) sichtbar += 1;
    });

    /* Die Zahl im Kopf nennt ALLE Treffer, nicht die dieser Seite — sonst
       stuende dort eine andere Zahl als auf dem Knopf, der hierher gefuehrt
       hat. */
    const zaehler = document.querySelector('.rc-collection-head__count');
    if (zaehler) {
      zaehler.textContent = this.strings.pill_hits
        ? this.satz('pill_hits', treffer.length).replace('[n]', treffer.length)
        : treffer.length + ' Produkte';
    }

    this.rasterHinweis(raster, sichtbar, treffer.length);

    /* Steht auf dieser Seite keine einzige passende Anlage, ist die Seite fuer
       den Kunden wertlos — er sieht ein leeres Raster und daneben „260
       Treffer". Dann wird die erste Seite gesucht, auf der Treffer stehen. */
    if (sichtbar === 0 && treffer.length > 0) this.springeZuTreffern(raster, treffer);
  }

  /* Die Seitenzahl kennt nur der Server. Diese Ansicht liefert die Handles der
     Collection in Server-Reihenfolge, mit den aktiven Filtern — rund 10 bis
     23 kB. Daraus laesst sich ausrechnen, auf welcher Seite ein Treffer steht.
     Die Alternative waere gewesen, die Rasterabschnitte der Folgeseiten zu
     laden: 196 kB je Seite, bei zwoelf Seiten ueber zwei Megabyte. */
  async springeZuTreffern(raster, treffer) {
    const params = new URLSearchParams(location.search);
    if (params.has('kseite')) return;          // schon einmal gesprungen
    const naechste = document.querySelector('.rc-pagination a[rel="next"], .rc-pagination__next');
    /* Nur PRODUKTkacheln zaehlen: das Raster enthaelt auf Seite 1 zusaetzlich
       eine Werbekachel. Mit ihr kaeme 25 statt 24 heraus, und die Rechnung
       Position -> Seite liefe mit jeder Seite weiter auseinander. */
    const proSeite = naechste
      ? [...raster.children].filter((e) => e.querySelector('a[href*="/products/"]')).length
      : 0;
    if (!proSeite) return;                     // letzte Seite, nichts zu holen

    const abfrage = new URLSearchParams(location.search);
    abfrage.delete('page');
    abfrage.set('view', 'handles');
    let alle = [];
    try {
      for (let seite = 1; seite <= 6; seite += 1) {
        abfrage.set('page', seite);
        /* OHNE `Accept: application/json` — mit dem Header antwortet Shopify
           mit seiner eigenen Collection-API statt mit dieser Ansicht, und die
           Antwort enthaelt die Handle-Liste nicht. */
        const r = await fetch(`${location.pathname}?${abfrage.toString()}`);
        if (!r.ok) return;
        const d = await r.json();
        alle = alle.concat(d.h || []);
        if (seite >= (d.seiten || 1)) break;
      }
    } catch {
      return;                                   // ohne Liste bleibt der Hinweis
    }
    if (!alle.length) return;

    const passt = new Set(treffer.map((a) => (a.u || '').split('?')[0].replace('/products/', '')));
    /* Nicht die ERSTE Seite mit einem Treffer, sondern die mit den MEISTEN.
       Der erste Versuch sprang auf Seite 2 — dort stand genau eine der 58
       Anlagen, also wieder eine fast leere Seite. Bei 58 Treffern auf elf
       Seiten lohnt der Sprung nur, wenn er zur dichtesten fuehrt. */
    const proSeiteZahl = new Map();
    alle.forEach((h, i) => {
      if (!passt.has(h)) return;
      const s = Math.floor(i / proSeite) + 1;
      proSeiteZahl.set(s, (proSeiteZahl.get(s) || 0) + 1);
    });
    if (!proSeiteZahl.size) return;

    const jetzt = parseInt(new URLSearchParams(location.search).get('page'), 10) || 1;
    let ziel = jetzt;
    let beste = -1;
    [...proSeiteZahl.entries()]
      .sort((a, b) => b[1] - a[1] || a[0] - b[0])
      .slice(0, 1)
      .forEach(([s, n]) => { ziel = s; beste = n; });
    if (ziel === jetzt || beste <= 0) return;
    const ziel_params = new URLSearchParams(location.search);
    ziel_params.set('page', ziel);
    ziel_params.set('kseite', '1');            // verhindert eine Sprungschleife
    location.replace(`${location.pathname}?${ziel_params.toString()}`);
  }

  /* Warum diese Zeile noetig ist:

     Die Collection wird serverseitig in Seiten zu 24 Kacheln geliefert, und
     das Modul kann davon nur AUSBLENDEN, was nicht passt. Bei vier Raeumen
     stehen von 99 passenden Anlagen null auf Seite 1 — der Kunde sah eine
     leere Seite, waehrend der Kopf „99 Anlagen" meldete und die Blaetterung
     16 Seiten anbot. Ohne Erklaerung sieht das nach einem Defekt aus.

     Die eigentliche Loesung ist ein serverseitiger Filter auf
     `custom.anzahl_innengeraete` (in Search & Discovery anzulegen; der
     Konfigurator gibt den Parameter bereits mit). Dann kaeme die Seite schon
     gefiltert und waere zu 76 bis 100 Prozent gefuellt. Bis dahin sagt diese
     Zeile wenigstens die Wahrheit. */
  rasterHinweis(raster, sichtbar, gesamt) {
    let el = document.querySelector('[data-raster-hinweis]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'rc-config__rasterhinweis';
      el.setAttribute('data-raster-hinweis', '');
      el.setAttribute('role', 'status');
      raster.parentNode.insertBefore(el, raster);
    }
    if (!gesamt) {
      el.textContent = this.strings.grid_none || '';
      el.hidden = !this.strings.grid_none;
      return;
    }
    if (sichtbar >= gesamt) {
      el.hidden = true;
      return;
    }
    const schluessel = sichtbar === 0 ? 'grid_empty_page' : 'grid_partial';
    // „stehen 1 der 58" ist schief — dafuer gibt es eine Einzahlfassung.
    el.textContent = this.satz(schluessel, sichtbar)
      .replace('[sichtbar]', sichtbar)
      .replace('[n]', gesamt);
    el.hidden = false;
  }

  update() {
    this.persist();

    const hits = this.matches(this.state);

    /* Ein Knopf fuer beide Faelle (29.08.2026). Vorher standen hier eine
       Trefferpille und zwei CTAs nebeneinander, von denen immer einer
       verborgen war. */
    if (hits.length > 0) {
      /* Nur die Zahl, kein "ab"-Preis: der vollstaendige Index fuehrt bewusst
         keine Preise, und ein Minimum ueber ein Teilfenster waere kein
         ehrliches "ab". */
      this.ctaText.textContent = this.satz('cta', hits.length)
        .replace('{count}', hits.length)
        .replace('[n]', hits.length);
      this.cta.classList.remove('rc-config__cta--keine');
      this.cta.href = this.buildUrl();
      this.suggestionEl.hidden = true;
    } else {
      this.ctaText.textContent = this.strings.cta_none || this.strings.pill_none;
      this.cta.classList.add('rc-config__cta--keine');
      this.cta.href = '/pages/beratung';
      this.showSuggestion();
      if (window.rcTrack) {
        window.rcTrack('configurator_zero_results', { anzahl: this.state.n, kw_liste: this.state.rooms.join(';') });
      }
    }
  }

  /* Vorschlag bei null Treffern.

     Die Kandidaten muessen dieselbe Geraetezahl haben wie die Auswahl. Vorher
     stand hier `a.n >= this.state.n` — die alte „mindestens"-Regel. Damit
     schlug der Tipp Anlagen vor, die unter der neuen Regel gar nicht
     erreichbar sind: bei vier Raeumen ein Fuenffachsystem. Ein Ausweg, der
     nirgendwo hinfuehrt, ist schlimmer als keiner. */
  showSuggestion() {
    const candidates = this.anlagen.filter((a) => a.n === this.state.n);
    if (!candidates.length) {
      this.suggestionEl.hidden = true;
      return;
    }
    const total = this.sum();
    const nearest = candidates.reduce((best, a) => (Math.abs(a.kw - total) < Math.abs(best.kw - total) ? a : best));
    const count = candidates.filter((a) => Math.abs(a.kw - nearest.kw) < 0.05).length;
    this.suggestionEl.textContent = this.satz('suggestion', count)
      .replace('[kw]', this.formatKw(nearest.kw))
      .replace('[n]', count);
    this.suggestionEl.hidden = false;
  }

  buildUrl() {
    const kwList = this.state.rooms.map((kw) => this.formatKw(kw)).join(';');
    /* Bei einem Raum fuehrt der Knopf in die Monosplit-Collection, nicht in
       die Multisplit-Liste — sonst verspraeche er Treffer, die dort nicht
       stehen. Der Produkttyp-Filter greift dort serverseitig und liefert eine
       dicht besetzte Seite. */
    const einRaum = this.state.n === 1;
    const monoUrl = this.dataset.monoUrl || '/collections/split-klimaanlagen';
    const base = einRaum ? monoUrl : (this.onTarget ? location.pathname : this.dataset.targetUrl);
    const params = new URLSearchParams(!einRaum && this.onTarget ? location.search : '');
    params.set('innengeraete', this.state.n);
    params.set('kw', kwList);

    /* Die Marken laufen ueber Shopifys eigenen Hersteller-Facetten-Parameter,
       nicht ueber einen eigenen. Der wird serverseitig ausgewertet — die
       Collection liefert dann schon nur noch die gewaehlten Marken, statt dass
       das Modul sie im Browser wieder ausblenden muesste. Am 29.08.2026
       geprueft: `filter.p.vendor=Bosch` schraenkt die Liste tatsaechlich ein.
       Nebeneffekt, der erwuenscht ist: die Filterleiste der Collection zeigt
       die Marke als aktiven Filter an. */
    params.delete('filter.p.vendor');
    (this.state.marken || []).forEach((m) => params.append('filter.p.vendor', m));

    /* Die Geraetezahl wird ebenfalls als Facette mitgegeben, obwohl
       `custom.anzahl_innengeraete` in Search & Discovery noch nicht als Filter
       angelegt ist (Stand 29.08.2026 geprueft: die Collection bietet Vendor,
       Produkttyp, Kuehlleistung, Raumgroesse, Energieklasse, dB, Heizfunktion,
       WLAN, Montage und Lieferzeit an, die Geraetezahl nicht). Shopify
       ignoriert unbekannte Filterparameter stillschweigend — der Link
       funktioniert also heute wie bisher und schraenkt von selbst richtig ein,
       sobald der Filter im Admin angelegt wird. Mehrere Werte, weil die Regel
       ignoriert unbekannte Filterparameter stillschweigend. */
    /* GENAU EIN Wert, seit die Regel exakt filtert; vorher standen hier alle
       Zahlen von n aufwaerts, was der Regel widersprochen haette, sobald der
       Filter im Admin angelegt ist. */
    params.delete('filter.p.m.custom.anzahl_innengeraete');
    if (einRaum) {
      params.set('filter.p.product_type', 'Monosplit-Klimaanlage');
    } else {
      params.append('filter.p.m.custom.anzahl_innengeraete', this.state.n);
    }
    /* Anker aufs Produktraster: Ohne ihn landete der Besucher am Seitenanfang
       der Ergebnisseite — und dort steht zuerst wieder der Konfigurator. Wer
       gerade „Ergebnisse anzeigen" gedrueckt hat, las das als „hat nicht
       geklappt". #CollectionContent traegt das Raster der Collection-Seite. */
    return `${base}?${params.toString()}#CollectionContent`;
  }

  onApply(event) {
    if (window.rcTrack) {
      window.rcTrack('configurator_apply', {
        anzahl: this.state.n,
        kw_liste: this.state.rooms.join(';'),
        summe_kw: this.sum(),
        treffer: this.matches(this.state).length,
      });
    }
  }


  /* renderHits(), renderResults() und restoreGrid() sind am 29.08.2026
     entfallen.

     renderHits() zeigte acht Kachelvorschauen unter dem Konfigurator und traf
     dabei fast nie das Kernsortiment — bei drei Innengeraeten steht die
     guenstigste Mitsubishi erst auf Platz 38.

     renderResults() ersetzte auf der Ziel-Collection das ganze Raster durch
     selbst gebaute Kacheln aus der Nutzlast. Das ging nur, solange die
     Nutzlast Titel, Bild und Preis mitfuehrte — und damit hoechstens 250
     Anlagen. Der vollstaendige Index traegt bewusst keine Preise (die muessen
     live aus dem Katalog kommen), also kann er keine Kacheln fuellen. Statt
     einer halben Liste mit eingefrorenen Preisen laedt der Knopf jetzt die
     Collection normal; filtereRaster() blendet dort aus, was nicht passt, und
     die Seitenblaetterung bleibt heil. */
}

customElements.define('rc-configurator', RcConfigurator);
}
